import translate from '@translate';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { RootStoreType } from 'src/types/store';
import { checkFormValidity } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import { GeisPackingType, TransportPackageType } from 'src/types/transportPackageType';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { addMessage } from 'src/resources/alertMessages/reducer';
import { setLocationParams } from 'src/resources/location/reducer';
import {
    clearTransportPackageTypeDetail,
    createTransportPackageTypeDetailFetch,
    deleteTransportPackageTypeDetailFetch,
    getTransportPackageTypeDetailFetch,
    updateTransportPackageTypeDetailFetch,
} from 'src/resources/transportPackageTypeDetail/reducer';
import api from '@api';
import TopBlock from './TopBlock';
import Detail from './Detail';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import deleteEmptyKeysAndValues from 'src/utils/deleteEmptyKeysAndValues';
import { FormErrorsType, OnChangeType } from 'src/types/global';
import { EditKeysType } from './constants';

const initData: TransportPackageType = {
    SSRC_CODE: '',
    NAME: '',
    TYPE: 'pallet',
    GEIS_PACKING_TYPE_CODE: null,
    ACTIVE: 1,
    SORT_ORDER: 0,
};

const TransportPackageTypeDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { ssrcCode } = useParams();
    const newItem = !ssrcCode;
    const formRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.transportPackageTypeDetail?.data);
    const loading = useSelector((state: RootStoreType) => state.transportPackageTypeDetail?.loading);
    const deleted = useSelector((state: RootStoreType) => state.transportPackageTypeDetail?.deleted);
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);

    const [editData, setEditData] = useState<TransportPackageType>(initData);
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);
    const [geisPackingTypeCodes, setGeisPackingTypeCodes] = useState<ConstantsCodeType[]>([]);

    const permissionCreate = useCheckPermission('TRANSPORT_PACKAGE_TYPE.CREATE');
    const permissionUpdate = useCheckPermission('TRANSPORT_PACKAGE_TYPE.UPDATE');
    const permissionDelete = useCheckPermission('TRANSPORT_PACKAGE_TYPE.DELETE');
    const disableEdit = newItem ? !permissionCreate : !permissionUpdate;
    const backUrl = '/transports/package-types/';

    useEffect(() => {
        if (ssrcCode) {
            dispatch(
                setLocationParams({
                    name: translate('transportPackageTypeDetail.title', { ssrcCode }),
                    backUrl,
                })
            );
        } else {
            dispatch(setLocationParams({ name: translate('transportPackageTypeDetail.titleNew'), backUrl }));
        }
    }, [ssrcCode, data?.SSRC_CODE]);

    useEffect(() => {
        if (ssrcCode) {
            dispatch(getTransportPackageTypeDetailFetch(ssrcCode));
        } else {
            setEditData(initData);
        }
        const loadGeis = async () => {
            try {
                const res = await api('get', 'api/secure/transports/geis-packing-types?active=1');
                const rows: GeisPackingType[] = Array.isArray(res.data?.data)
                    ? res.data.data
                    : Array.isArray(res.data)
                      ? res.data
                      : [];
                setGeisPackingTypeCodes(
                    rows.map((row) => ({
                        label: `${row.CODE} — ${row.NAME}`,
                        value: row.CODE,
                    }))
                );
            } catch {
                setGeisPackingTypeCodes([]);
            }
        };
        void loadGeis();
        return () => {
            dispatch(clearTransportPackageTypeDetail());
        };
    }, [ssrcCode]);

    useEffect(() => {
        if (data) {
            setEditData({ ...data });
        }
        if (!ssrcCode && data?.SSRC_CODE) {
            navigate(`/transports/package-types/${data.SSRC_CODE}`);
        }
    }, [data]);

    useEffect(() => {
        if (deleted) {
            navigate('/transports/package-types');
        }
    }, [deleted]);

    const onChange: OnChangeType<EditKeysType> = (key, value, error) => {
        setEditData({ ...editData, [key]: value });
        setEditErrors({ ...editErrors, [key]: error });
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(formRef);
        if (!Object.keys(newFormErrors).length) {
            const formatData = newItem ? deleteEmptyKeysAndValues(editData) : editData;
            if (newItem) {
                dispatch(createTransportPackageTypeDetailFetch(formatData as TransportPackageType));
            } else if (ssrcCode) {
                const { SSRC_CODE: _code, SYNCED_AT: _synced, ...payload } = formatData as TransportPackageType;
                dispatch(updateTransportPackageTypeDetailFetch({ ssrcCode, data: payload }));
            }
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText'),
                })
            );
        }
        setEditErrors(newFormErrors);
    };

    const handleDelete = () => {
        setConfirmParams({
            title: translate('transportPackageTypeDetail.deactivateConfirm'),
            action: () => {
                if (ssrcCode) dispatch(deleteTransportPackageTypeDetailFetch(ssrcCode));
            },
        });
    };

    return (
        <>
            <TopBlock
                newItem={newItem}
                disableEdit={disableEdit}
                permissionDelete={permissionDelete}
                handleSave={handleSave}
                handleDelete={handleDelete}
            />
            <form ref={formRef}>
                {loading && <Loading />}
                <Detail
                    onChange={onChange}
                    editData={editData}
                    editErrors={editErrors}
                    newItem={newItem}
                    disableEdit={disableEdit}
                    boolNumberCodes={boolNumberCodes}
                    geisPackingTypeCodes={geisPackingTypeCodes}
                />
            </form>
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onClose={() => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default TransportPackageTypeDetailContainer;
